import { useEffect, useState, useContext, useMemo } from "react";
import AuthContext from "../../contexts/auth-context";
import useAxios from "../../hooks/useAxios";
import { NavLink, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Pagination,
  CircularProgress,
  TextField,
  InputAdornment,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ArrowDropUp, ArrowDropDown } from "@mui/icons-material";
import { app_paths, toastify_status, PAGE_SIZE, urls } from "../../components/config/variables";
import Toastify from "../../components/ui/Toastify";
import TableComponent from "../../components/ui/Table";
import { motion } from 'framer-motion';
import { pageVariants, pageTransition } from "../../constants/GlobalStyles";
import { validateDrugsInventory } from "../../Utils/validateHttpResponse";
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';



const InventoryPage = () => {
  const { operation } = useAxios();
  const { token } = useContext(AuthContext);
  const location = useLocation()
  const lowStock = location.state?.lowStock;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  const [lowStockFilter, setLowStockFilter] = useState(lowStock ? lowStock : false);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);

  // Sorting State
  const [sortBy, setSortBy] = useState("quantity");
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnKey);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    setLoading(true);

    const params = new URLSearchParams();
    if (isSearch) {
      params.set('drug_name', searchQuery);
    }
    if (lowStockFilter) {
      params.set('low_stock', lowStockFilter);
    }

    operation(
      {
        method: "GET",
        url: `${urls.GET_PHARMACY_DRUGS}?${params}`,
        headers: { Authorization: `token ${token}` },
      },
      (res) => {
        setLoading(false);
        if(validateDrugsInventory(res)){
          setItems(res);
        } else {
          Toastify({message: "Invalid response for medicines inventory.", state: toastify_status.ERROR});
        }
      },
      () => {
        setLoading(false);
        Toastify("Failed to load medicines inventory.", toastify_status.ERROR);
      }
    );
    // eslint-disable-next-line
  }, [isSearch, lowStockFilter]);

  // Sort
  const filteredData = useMemo(() => {
    let filtered = [...items];

    filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      if (!isNaN(aVal) && !isNaN(bVal)) {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      } else if (aVal && bVal) {
        return sortDirection === "asc"
          ? aVal.toString().localeCompare(bVal.toString())
          : bVal.toString().localeCompare(aVal.toString());
      }
      return 0;
    });

    return filtered;
  }, [items, sortBy, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const indexOfLastRow = currentPage * PAGE_SIZE;
  const indexOfFirstRow = indexOfLastRow - PAGE_SIZE;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  const handleChangePage = (_, value) => {
    setCurrentPage(value);
  };

  const tableHeaders = [
    { label: "Medicine Name", key: "drug_name" },
    { label: "Stock Quantity", key: "quantity" },
    { label: "Unit Price", key: "price" },
    { label: "Total Value", key: "total_price" },
  ];

  const handleSearchClick = (event) => {
    if (searchQuery !== '') {
      event.preventDefault();
      setIsSearch(true);
    }
  };

  const handleClearSearch = () => {
    setIsSearch(false);
    setSearchQuery('');
  };

  useEffect(() => {
    if (isSearch && searchQuery === '') {
      handleClearSearch();
    }
    // eslint-disable-next-line
  }, [searchQuery, isSearch]);

  const handleChangeLowStockfilter = (event) => {
    setLowStockFilter(event.target.checked,);
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
    >

      {/* top bar */}
                <Box
                    sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    mb: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "12px",
                    backgroundColor: "background.paper",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                  }}
                >
                    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 100,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "text.secondary",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: ".5px",
                            }}
                          >
                            Total Medicines
                          </Typography>

                          {loading
                            ? <CircularProgress size={18} />
                            : <Typography
                              sx={{
                                fontSize: "18px",
                                fontWeight: 700,
                                color: "primary.main",
                                lineHeight: 1.1,
                              }}
                            >
                              {items.length}
                            </Typography>}
                        </Box>

                        <Divider orientation="vertical" flexItem />

                        {/* Search */}
                        <TextField
                            label="Search"
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(event) => event.key === "Enter" && handleSearchClick(event)}
                            InputProps={{
                                startAdornment: searchQuery && (
                                    <InputAdornment position="start">
                                        <IconButton size="small" onClick={handleClearSearch}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleSearchClick}>
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                      {/**/}
                      <FormControlLabel
            control={
              <Checkbox checked={lowStockFilter} onChange={handleChangeLowStockfilter} name="gilad" />
            }
            label="Low Stock Medicines"
          />
                    </Box>

                    {/* Upload Price Button */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Divider orientation="vertical" flexItem />
                    <NavLink to={app_paths.UPLOAD_PRICES_PAGE} style={{ textDecoration: "none" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 2,
                          py: 1,
                          borderRadius: "10px",
                          bgcolor: "primary.main",
                          color: "white",
                          cursor: "pointer",
                          transition: ".2s",
                          "&:hover": { opacity: 0.9 },
                        }}
                      >
                        <AddCircleOutlineIcon />
                        <Typography fontWeight={600}>Upload Prices</Typography>
                      </Box>
                    </NavLink>
                  </Box>
                </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <TableComponent
          tableHead={tableHeaders.map((h) => (
            <Box
              key={h.key}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={() => handleSort(h.key)}
            >
              {h.label}
              {sortBy === h.key && (
                sortDirection === "asc" ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />
              )}
            </Box>
          ))}
          tableBody={currentRows}
          bodyCells={tableHeaders.map((h) => h.key)}
          bodyCellsCustomSymbol={{ price: "$", total_price: "$" }}
          customStyle={{
            tableContainer: {
              maxHeight: "600px",
              overflowY: "auto",
              borderRadius: 1.5,
            },
            headerTableCell: (cell) => ({
              textAlign: "center",
              border: "1px solid #d9d9d9",
              fontSize: "16px",
              fontWeight: "600",
              color: "#000000c7",
              backgroundColor: "#F5F5F5",
              padding: "12px 0",
              width: cell.key === "drug_name" ? "40%" : "20%",
            }),
            row: {
              transition: "0.2s",
              border: "1px solid #d9d9d9",
              backgroundColor: "#FCFEFE",
              "&:hover": { backgroundColor: "#f9f9f9" },
            },
            tableCell: {
              border: "1px solid #d9d9d9",
              textAlign: "center",
              padding: "14px",
              fontWeight: "500",
            },
          }}
        />
      )}

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handleChangePage}
            color="primary"
            disabled={loading}
          />
        </Box>
      )}
    </motion.div>
  );
};

export default InventoryPage;